import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AdminContext = createContext({ isAdmin: false, loading: true, login: () => {}, logout: () => {} });

export const useAdmin = () => useContext(AdminContext);

export function AdminProvider({ children }) {
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => localStorage.getItem("admin_token") || "");

  useEffect(() => {
    const check = async () => {
      try {
        const userToken = localStorage.getItem("user_session_token") || "";
        const res = await axios.get(`${API}/auth/admin-check`, {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(userToken ? { "X-User-Token": userToken } : {}),
          }
        });
        if (res.data.authenticated) {
          setIsAdmin(true);
          if (res.data.auto_granted && res.data.token) {
            localStorage.setItem("admin_token", res.data.token);
            setToken(res.data.token);
          }
        } else {
          setIsAdmin(false);
        }
      } catch { setIsAdmin(false); }
      finally { setLoading(false); }
    };
    check();
  }, [token]);

  const login = async (password) => {
    const res = await axios.post(`${API}/auth/admin-login`, { password });
    if (res.data.success) {
      localStorage.setItem("admin_token", res.data.token);
      setToken(res.data.token);
      setIsAdmin(true);
      return true;
    }
    return false;
  };

  const logout = async () => {
    try { await axios.post(`${API}/auth/admin-logout`); } catch {}
    localStorage.removeItem("admin_token");
    setToken("");
    setIsAdmin(false);
  };

  // Re-check admin when user logs in/out
  useEffect(() => {
    const handler = () => recheckAdmin();
    window.addEventListener("user-auth-changed", handler);
    return () => window.removeEventListener("user-auth-changed", handler);
  }, []);

  const recheckAdmin = () => {
    setLoading(true);
    const userToken = localStorage.getItem("user_session_token") || "";
    const adminToken = localStorage.getItem("admin_token") || "";
    axios.get(`${API}/auth/admin-check`, {
      headers: {
        ...(adminToken ? { Authorization: `Bearer ${adminToken}` } : {}),
        ...(userToken ? { "X-User-Token": userToken } : {}),
      }
    }).then(res => {
      if (res.data.authenticated) {
        setIsAdmin(true);
        if (res.data.auto_granted && res.data.token) {
          localStorage.setItem("admin_token", res.data.token);
          setToken(res.data.token);
        }
      } else {
        setIsAdmin(false);
      }
    }).catch(() => setIsAdmin(false))
    .finally(() => setLoading(false));
  };

  return (
    <AdminContext.Provider value={{ isAdmin, loading, login, logout, recheckAdmin }}>
      {children}
    </AdminContext.Provider>
  );
}
